import { ContentState, EditorState, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import { forwardRef, useEffect, useState } from 'react'
import { Editor } from 'react-draft-wysiwyg'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { ControllerRenderProps } from 'react-hook-form'

const DraftEditor: React.FC<any> = Editor as unknown as any

export type TextAreaProps = Omit<ControllerRenderProps, 'ref'> & {
  label?: string
  labelClassName?: string
  description?: string
  descriptionClassName?: string
  error?: string
  placeholder?: string
  defaultValue?: string
  hasBorder?: boolean
}

export const TextArea = forwardRef<any, TextAreaProps>(
  (
    {
      label,
      labelClassName,
      error,
      description,
      descriptionClassName,
      onChange,
      defaultValue,
      hasBorder,
      ...props
    }: TextAreaProps,
    ref
  ) => {
    const [editorState, setEditorState] = useState(EditorState.createEmpty())

    const onEditorStateChange = (editorState: EditorState) => {
      setEditorState(editorState)
      onChange(draftToHtml(convertToRaw(editorState.getCurrentContent())))
    }

    const handlePastedText = (
      text: string,
      _html: string,
      editorState: EditorState,
      onChange: (editorState: EditorState) => void
    ) => {
      editorState = EditorState.createWithContent(ContentState.createFromText(text))
      onChange(editorState)
      return false
    }

    useEffect(() => {
      if (defaultValue) {
        const blocksFromHtml = htmlToDraft(defaultValue)
        const contentState = ContentState.createFromBlockArray(blocksFromHtml.contentBlocks, blocksFromHtml.entityMap)
        const newEditorState = EditorState.createWithContent(contentState)
        setEditorState(newEditorState)
      }
    }, [defaultValue])

    return (
      <div className="flex flex-col gap-y-2">
        {label && (
          <span className={`text-white text-sm font-semibold ${labelClassName ? labelClassName : ''}`}>{label}</span>
        )}
        {description && (
          <p
            className={`text-xs leading-5 text-white text-opacity-70 ${
              descriptionClassName ? descriptionClassName : ''
            }`}
          >
            {description}
          </p>
        )}

        <DraftEditor
          {...props}
          ref={ref}
          spellCheck
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
          handlePastedText={handlePastedText}
          toolbarClassName="h-12 rounded"
          editorClassName="bg-white px-2 rounded"
          wrapperClassName={`h-48 rounded-md flex flex-col ${
            hasBorder ? 'border border-navy-900 border-opacity-20' : ''
          }`}
          toolbar={{
            options: ['inline'],
            inline: {
              inDropdown: false,
              options: ['bold', 'italic', 'underline'],
            },
          }}
        />
        {error && <div className="text-boldOrange -mt-1 font-medium">{error}</div>}
      </div>
    )
  }
)
